import React from 'react';
import { showToastMessage, preventKeyInputs } from '../../../../../../../utils/commonFunctions';
import styles from './GeoFeaturePanel.module.scss';

const limitDecimalPlaces = (valStr: string, maxDecimals: number = 8): string => {
  if (!valStr.includes('.')) return valStr;
  const parts = valStr.split('.');
  if (parts[1].length > maxDecimals) {
    return `${parts[0]}.${parts[1].substring(0, maxDecimals)}`;
  }
  return valStr;
};

interface VertexRowProps {
  index: number;
  lat: string;
  lng: string;
  onUpdate: (index: number, field: 'lat' | 'lng', val: string) => void;
  onRemove: (index: number) => void;
}

/**
 * Isolated, memoized row component for editing a single polygon vertex (lat/lng).
 * Prevents sibling rows from re-rendering when typing in one row.
 */
export const VertexRow: React.FC<VertexRowProps> = React.memo(({
  index,
  lat,
  lng,
  onUpdate,
  onRemove,
}) => {
  const handleNumericKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    value: string,
    field: 'lat' | 'lng',
    min: number,
    max: number,
    label: string
  ) => {
    if (!preventKeyInputs(e, ['e', 'E', '+'])) return;

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const numValue = parseFloat(value);

      let step = 1;
      if (e.shiftKey) {
        step = 10;
      } else if (e.altKey || e.ctrlKey) {
        step = 0.1;
      }

      if (isNaN(numValue)) {
        onUpdate(index, field, e.key === 'ArrowUp' ? step.toString() : (-step).toString());
      } else {
        const decimals = value.includes('.') ? value.split('.')[1].length : 0;
        const targetDecimals = Math.min(Math.max(decimals, step === 0.1 ? 1 : 0), 10);
        const newValue = e.key === 'ArrowUp' ? numValue + step : numValue - step;

        if (newValue < min || newValue > max) {
          showToastMessage('info', `${label} must be between ${min} and ${max}°`);
          return;
        }

        onUpdate(index, field, parseFloat(newValue.toFixed(targetDecimals)).toString());
      }
    }
  };

  const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = limitDecimalPlaces(e.target.value);
    const num = parseFloat(v);
    if (!isNaN(num) && (num < -90 || num > 90)) {
      showToastMessage('info', 'Latitude must be between -90 and 90°');
      return;
    }
    onUpdate(index, 'lat', v);
  };

  const handleLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = limitDecimalPlaces(e.target.value);
    const num = parseFloat(v);
    if (!isNaN(num) && (num < -180 || num > 180)) {
      showToastMessage('info', 'Longitude must be between -180 and 180°');
      return;
    }
    onUpdate(index, 'lng', v);
  };

  return (
    <div className={styles.pointRow}>
      <span className={styles.pl}>P{String(index + 1).padStart(2, '0')}</span>
      <div className={styles.input}>
        <input
          value={lat}
          onKeyDown={e => handleNumericKeyDown(e, lat, 'lat', -90, 90, `Latitude of P${index + 1}`)}
          onChange={handleLatChange}
          placeholder="Lat"
          type="number"
          step="0.0001"
        />
      </div>
      <div className={styles.input}>
        <input
          value={lng}
          onKeyDown={e => handleNumericKeyDown(e, lng, 'lng', -180, 180, `Longitude of P${index + 1}`)}
          onChange={handleLngChange}
          placeholder="Long"
          type="number"
          step="0.0001"
        />
      </div>
      <button className={styles.rm} type="button" onClick={() => onRemove(index)}>×</button>
    </div>
  );
});

VertexRow.displayName = 'VertexRow';
